import { useState, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { Plus, Check } from 'lucide-react'
import { Dropdown } from 'antd'
import type { ProjectLabel } from '@bandos/shared'
import { DeleteButton } from '../Buttons/DeleteButton'
import { Z_INDEX } from '../../../styles/theme'
import {
  DropdownContent,
  LabelList,
  LabelItem,
  DeleteButtonWrapper,
  Checkbox,
  LabelColorDot,
  LabelText,
  Divider,
  CreateButton,
  CreateForm,
  FormSection,
  FormLabel,
  StyledInput,
  ColorPalette,
  PencilIcon,
  DropdownWrapper,
  ColorOption,
  CustomColorInput,
  ButtonGroupContainer,
  AntButton,
} from './styles/LabelSelectorStyles'

interface LabelSelectorProps {
  labels: ProjectLabel[]
  selectedLabelIds: string[]
  onSelectLabel: (labelId: string) => void
  onCreateLabel: (labelText: string, color: string) => Promise<void>
  onDeleteLabel?: (labelId: string) => Promise<void>
  children: React.ReactElement
}

// Pre-defined color palette
const LABEL_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
]

export function LabelSelector({
  labels,
  selectedLabelIds,
  onSelectLabel,
  onCreateLabel,
  onDeleteLabel,
  children,
}: LabelSelectorProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newLabelText, setNewLabelText] = useState('')
  const [selectedColor, setSelectedColor] = useState(LABEL_COLORS[0].value)
  const [open, setOpen] = useState(false)
  const colorInputRef = useRef<HTMLInputElement>(null)

  // Reset form when closing
  useEffect(() => {
    if (!open) {
      setIsCreating(false)
      setIsSubmitting(false)
      setNewLabelText('')
      setSelectedColor(LABEL_COLORS[0].value)
    }
  }, [open])

  const handleCustomColorClick = () => {
    colorInputRef.current?.click()
  }

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value
    setSelectedColor(color)
  }

  const handleCreate = async () => {
    if (!newLabelText.trim() || isSubmitting) return

    const labelText = newLabelText.trim()
    const color = selectedColor
    setIsSubmitting(true)
    setOpen(false)
    try {
      await onCreateLabel(labelText, color)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.labels', action: 'create_label' },
      })
      // Error handled by parent - label creation failed silently
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLabelClick = (labelId: string) => {
    setOpen(false)
    onSelectLabel(labelId)
  }

  const handleDeleteLabel = async (e: React.MouseEvent, labelId: string) => {
    e.stopPropagation()
    if (onDeleteLabel) {
      await onDeleteLabel(labelId)
    }
  }

  const dropdownContent = (
    <DropdownContent>
      {!isCreating ? (
        <>
          <LabelList>
            {labels.length !== 0 &&
              labels.map((label) => {
                const isSelected = selectedLabelIds.includes(label.id)
                return (
                  <LabelItem
                    key={label.id}
                    $isSelected={isSelected}
                    onClick={() => handleLabelClick(label.id)}
                  >
                    <Checkbox $isChecked={isSelected} $color={label.color}>
                      {isSelected && <Check size={12} color="white" strokeWidth={3} />}
                    </Checkbox>
                    <LabelColorDot $color={label.color} />
                    <LabelText>{label.label_text}</LabelText>
                    {onDeleteLabel && (
                      <DeleteButtonWrapper onClick={(e) => e.stopPropagation()}>
                        <DeleteButton
                          onClick={(e) => handleDeleteLabel(e, label.id)}
                          title="Delete label from project"
                          size={12}
                        />
                      </DeleteButtonWrapper>
                    )}
                  </LabelItem>
                )
              })}
          </LabelList>
          <Divider />
          <CreateButton onClick={() => setIsCreating(true)}>
            <Plus size={14} />
            Create new label
          </CreateButton>
        </>
      ) : (
        <CreateForm>
          <FormSection>
            <FormLabel>Label name</FormLabel>
            <StyledInput
              value={newLabelText}
              onChange={(e) => setNewLabelText(e.target.value)}
              placeholder="MVP"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleCreate()
                } else if (e.key === 'Escape') {
                  setIsCreating(false)
                }
              }}
            />
          </FormSection>
          <FormSection>
            <FormLabel>Color</FormLabel>
            <ColorPalette>
              {LABEL_COLORS.map((color) => (
                <ColorOption
                  key={color.value}
                  $color={color.value}
                  $isSelected={selectedColor === color.value}
                  onClick={() => setSelectedColor(color.value)}
                  title={color.name}
                />
              ))}
              <ColorOption
                $color={selectedColor}
                $isSelected={!LABEL_COLORS.some((c) => c.value === selectedColor)}
                $isCustom={true}
                $customColor={selectedColor}
                onClick={handleCustomColorClick}
                title="Custom color"
              >
                {(!selectedColor || LABEL_COLORS.some((c) => c.value === selectedColor)) && (
                  <PencilIcon size={10} strokeWidth={2.5} />
                )}
                <CustomColorInput
                  ref={colorInputRef}
                  type="color"
                  value={
                    LABEL_COLORS.some((c) => c.value === selectedColor)
                      ? selectedColor
                      : selectedColor
                  }
                  onChange={handleCustomColorChange}
                />
              </ColorOption>
            </ColorPalette>
          </FormSection>
          <ButtonGroupContainer>
            <AntButton size="small" onClick={() => setIsCreating(false)}>
              Cancel
            </AntButton>
            <AntButton
              type="primary"
              size="small"
              onClick={handleCreate}
              disabled={!newLabelText.trim()}
            >
              Create
            </AntButton>
          </ButtonGroupContainer>
        </CreateForm>
      )}
    </DropdownContent>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={() => (
        <DropdownWrapper onClick={(e) => e.stopPropagation()}>{dropdownContent}</DropdownWrapper>
      )}
      trigger={['click']}
      placement="bottomLeft"
      getPopupContainer={() => document.body}
      styles={{ root: { zIndex: Z_INDEX.dropdown } }}
    >
      {children}
    </Dropdown>
  )
}
